import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Modal,
  Nav,
  Row,
  Spinner,
  Table,
  Form,
} from "react-bootstrap";
import {
  RefreshCw,
  List,
  Copy,
  Check,
  ExternalLink,
  Download,
  AlertTriangle,
} from "react-feather";
import { Button } from "react-bootstrap";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Link, useSearchParams } from "react-router";
import { useGet, useGetLazy } from "../../api";
import { LogLineText } from "./logFormatting";
import { ReportStatCard } from "../../components/stat-card";
import {
  IAutoCouponRunListResponse,
  IAutoCouponRun,
  IAutoCouponEvaluationListResponse,
  IAutoCouponTemplateListResponse,
  TAutoCouponLogLevel,
  TSessionRunPartStatus,
  AUTO_COUPON_COST_REPORT_MAX_DAYS,
  IAutoCouponRunCostReportResponse,
  IAutoCouponCostByDomainEntry,
  TAutoCouponCostFlag,
} from "../../interfaces";

const ALL_LOG_LEVELS: TAutoCouponLogLevel[] = [
  "error",
  "warn",
  "info",
  "debug",
  "verbose",
];

// ─── View badge (desktop / mobile / both) ────────────────────────────────────

const ViewBadge: React.FC<{ view: string | null | undefined }> = ({ view }) => {
  if (!view) return <span className="text-muted small">—</span>;
  const bg =
    view === "mobile" ? "warning" : view === "both" ? "secondary" : "info";
  return (
    <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>
      {view}
    </Badge>
  );
};

/**
 * pass / fail / not_tested badge for one half of a run's QA verdict.
 * `applicable` (derived from the run's `runMode`) distinguishes a half that
 * simply wasn't rated ("not tested") from one this run's mode never exercised
 * at all ("n/a") — e.g. a sku-only run's auto half, or an auto-only run's SKU
 * half. Without this, a partial-mode run always reads as an incomplete
 * review, even when the reviewer rated everything the run actually touched.
 */
const PartBadge: React.FC<{
  status: TSessionRunPartStatus;
  applicable: boolean;
  notTestedHint: string;
}> = ({ status, applicable, notTestedHint }) => {
  if (status === "not_tested") {
    if (!applicable) {
      return (
        <Badge
          bg="light"
          text="muted"
          title="This run's mode didn't exercise this half"
        >
          n/a
        </Badge>
      );
    }
    return (
      <Badge bg="light" text="dark" title={notTestedHint}>
        not tested
      </Badge>
    );
  }
  return status === "pass" ? (
    <Badge bg="success">pass</Badge>
  ) : (
    <Badge bg="danger">fail</Badge>
  );
};

// ─── Run detail modal ──────────────────────────────────────────────────────

const RunDetailModal: React.FC<{
  run: IAutoCouponRun | null;
  onClose: () => void;
}> = ({ run, onClose }) => {
  const [tab, setTab] = useState<"logs" | "evaluation">("logs");
  const [copiedField, setCopiedField] = useState<"id" | "url" | null>(null);
  const [levelFilter, setLevelFilter] =
    useState<TAutoCouponLogLevel[]>(ALL_LOG_LEVELS);
  const [tagFilter, setTagFilter] = useState<string[] | null>(null);
  const [logKeyword, setLogKeyword] = useState("");

  // Reset log filters whenever a different run is opened, so a prior run's
  // tag/level selection doesn't silently hide entries on the next one.
  useEffect(() => {
    setLevelFilter(ALL_LOG_LEVELS);
    setTagFilter(null);
    setLogKeyword("");
  }, [run?.correlationId]);

  const availableLogTags = React.useMemo(() => {
    const tags = new Set<string>();
    run?.logs?.forEach((entry) => {
      if (entry.tag) tags.add(entry.tag);
    });
    return Array.from(tags).sort();
  }, [run]);

  const selectedLogTags = tagFilter ?? availableLogTags;

  const toggleLogLevel = (level: TAutoCouponLogLevel) => {
    setLevelFilter((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const toggleLogTag = (tag: string) => {
    setTagFilter((prev) => {
      const base = prev ?? availableLogTags;
      return base.includes(tag)
        ? base.filter((t) => t !== tag)
        : [...base, tag];
    });
  };

  const filteredLogs = React.useMemo(() => {
    const keyword = logKeyword.trim().toLowerCase();
    return (run?.logs ?? []).filter((entry) => {
      if (!levelFilter.includes(entry.level)) return false;
      if (entry.tag && !selectedLogTags.includes(entry.tag)) return false;
      if (keyword && !entry.text.toLowerCase().includes(keyword)) return false;
      return true;
    });
  }, [run, levelFilter, selectedLogTags, logKeyword]);

  const { data: evalData, loading: evalLoading } =
    useGet<IAutoCouponEvaluationListResponse>(
      run
        ? `/auto-coupons/evaluations?correlationId=${encodeURIComponent(run.correlationId)}&limit=1`
        : null
    );

  const evaluation = evalData?.evaluations?.[0] ?? null;

  const { data: templateData } = useGet<IAutoCouponTemplateListResponse>(
    evaluation
      ? `/auto-coupons/templates?domain=${encodeURIComponent(evaluation.domain)}&country=${encodeURIComponent(evaluation.country)}&limit=1`
      : null
  );

  const skuSelectors = templateData?.templates?.[0]?.skuSelectors ?? null;

  const levelBadgeBg: Record<string, string> = {
    error: "danger",
    warn: "warning",
    info: "info",
    debug: "dark",
    verbose: "secondary",
  };

  const copyToClipboard = (text: string, field: "id" | "url") => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const bestSaving =
    evaluation?.codes.reduce(
      (max, c) => (c.success && c.savings > max ? c.savings : max),
      0
    ) ?? 0;

  return (
    <Modal show={!!run} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <span>{run?.domain}</span>
          <span className="font-monospace small text-muted">
            {run?.correlationId?.slice(0, 8)}…
          </span>
          <Button
            size="sm"
            variant="link"
            className="p-0 text-muted"
            title="Copy full correlation ID"
            onClick={() => run && copyToClipboard(run.correlationId, "id")}
          >
            {copiedField === "id" ? <Check size={13} /> : <Copy size={13} />}
          </Button>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Who ran this */}
        <div className="mb-3 small">
          <span className="text-muted me-1">Run by:</span>
          {run?.origin === "extension" ? (
            <Badge bg="light" text="dark">
              User (extension)
            </Badge>
          ) : (
            <Badge bg="dark">
              Agentic{run?.origin ? ` (${run.origin})` : ""}
            </Badge>
          )}
          {run?.sessionCaptureOnly && (
            <Badge
              bg="secondary"
              className="ms-1"
              title="No AI stages ran and no coupon codes were attempted — this run only captured the browser session"
            >
              Session capture only
            </Badge>
          )}
          {run?.userEmail && <span className="ms-2">{run.userEmail}</span>}
        </div>

        {run?.abandoned && (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <AlertTriangle size={16} />
            <span>
              <strong>Run abandoned.</strong>{" "}
              {run.abandonedReason ??
                "The tab went away mid-run, or a Pass/Fail verdict was never submitted."}
            </span>
          </Alert>
        )}

        {/* Run meta — surfaced straight from the run doc (view / country /
            captured session), independent of the linked evaluation. */}
        {run && (
          <Row className="mb-3 g-2">
            <Col xs={6} md={3}>
              <small className="text-muted d-block">View</small>
              <ViewBadge view={run.templateView} />
            </Col>
            <Col xs={6} md={3}>
              <small
                className="text-muted d-block"
                title="Which half(s) of the checkout flow this run exercised"
              >
                Run mode
              </small>
              {run.runMode ? (
                <Badge bg="light" text="dark" className="border">
                  {run.runMode}
                </Badge>
              ) : (
                <span className="text-muted small">—</span>
              )}
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Country</small>
              <strong className="small">{run.country || "—"}</strong>
            </Col>
            <Col xs={6} md={3}>
              <small
                className="text-muted d-block"
                title="Extension build that produced this run — set once its evaluation completes"
              >
                Extension
              </small>
              <strong className="small">
                {run.extensionVersion ? `v${run.extensionVersion}` : "—"}
              </strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Session</small>
              {run.hasSession ? (
                <Badge bg="success" title={run.sessionCapturedAt ?? undefined}>
                  Captured
                </Badge>
              ) : (
                <span className="text-muted small">None</span>
              )}
              {run.hasSession && run.sessionCapturedAt && (
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {new Date(run.sessionCapturedAt).toLocaleString()}
                </div>
              )}
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Checkout URL</small>
              {run.checkoutUrl ? (
                <span className="d-inline-flex align-items-center gap-1">
                  <span
                    className="font-monospace small"
                    title={run.checkoutUrl}
                  >
                    {run.checkoutUrl.length > 32
                      ? `${run.checkoutUrl.slice(0, 32)}…`
                      : run.checkoutUrl}
                  </span>
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 text-muted"
                    title="Copy full checkout URL"
                    onClick={() =>
                      run.checkoutUrl && copyToClipboard(run.checkoutUrl, "url")
                    }
                  >
                    {copiedField === "url" ? (
                      <Check size={13} />
                    ) : (
                      <Copy size={13} />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 text-muted"
                    title="Open checkout URL in a new tab"
                    href={run.checkoutUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink size={13} />
                  </Button>
                </span>
              ) : (
                <span className="text-muted small">—</span>
              )}
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">AI cost</small>
              <strong className="small">
                {run.aiCost != null ? `$${run.aiCost.toFixed(2)}` : "—"}
              </strong>
            </Col>
          </Row>
        )}
        {/* Tab bar */}
        <div className="d-flex gap-2 mb-3 border-bottom pb-2">
          <Button
            size="sm"
            variant={tab === "logs" ? "primary" : "outline-secondary"}
            onClick={() => setTab("logs")}
          >
            Logs ({run?.logs?.length ?? 0})
          </Button>
          <Button
            size="sm"
            variant={tab === "evaluation" ? "primary" : "outline-secondary"}
            onClick={() => setTab("evaluation")}
          >
            Evaluation
          </Button>
        </div>

        {/* Logs tab */}
        {tab === "logs" && (
          <>
            <div className="bg-light border rounded p-2 mb-2">
              <div className="d-flex flex-wrap gap-2 mb-2">
                {ALL_LOG_LEVELS.map((level) => {
                  const active = levelFilter.includes(level);
                  return (
                    <Badge
                      key={level}
                      bg={
                        active ? (levelBadgeBg[level] ?? "secondary") : "light"
                      }
                      text={
                        !active
                          ? "muted"
                          : level === "warn"
                            ? "dark"
                            : undefined
                      }
                      role="button"
                      className="text-uppercase"
                      style={{ cursor: "pointer", opacity: active ? 1 : 0.5 }}
                      onClick={() => toggleLogLevel(level)}
                    >
                      {level}
                    </Badge>
                  );
                })}
              </div>
              {availableLogTags.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {availableLogTags.map((tag) => {
                    const active = selectedLogTags.includes(tag);
                    return (
                      <Badge
                        key={tag}
                        bg={active ? "secondary" : "light"}
                        text={active ? undefined : "muted"}
                        role="button"
                        style={{ cursor: "pointer", opacity: active ? 1 : 0.5 }}
                        onClick={() => toggleLogTag(tag)}
                      >
                        {tag}
                      </Badge>
                    );
                  })}
                </div>
              )}
              <Form.Control
                size="sm"
                placeholder="Filter log text…"
                value={logKeyword}
                onChange={(e) => setLogKeyword(e.target.value)}
              />
            </div>

            <div className="small text-muted mb-2">
              Showing {filteredLogs.length} of {run?.logs?.length ?? 0} entries
            </div>

            <div style={{ maxHeight: 460, overflowY: "auto" }}>
              {(run?.logs?.length ?? 0) === 0 && (
                <p className="text-muted small">No log entries.</p>
              )}
              {(run?.logs?.length ?? 0) > 0 && filteredLogs.length === 0 && (
                <p className="text-muted small">
                  No log entries match the current filters.
                </p>
              )}
              {filteredLogs.map((entry, i) => (
                <div
                  key={i}
                  className="d-flex align-items-start gap-2 small mb-1"
                >
                  <Badge
                    bg="light"
                    text="muted"
                    className="font-monospace flex-shrink-0"
                    style={{ minWidth: 78, fontWeight: 400 }}
                  >
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </Badge>
                  <Badge
                    bg={levelBadgeBg[entry.level] ?? "secondary"}
                    text={entry.level === "warn" ? "dark" : undefined}
                    className="text-uppercase flex-shrink-0"
                    style={{ minWidth: 56, textAlign: "center" }}
                  >
                    {entry.level}
                  </Badge>
                  {entry.tag && (
                    <Badge
                      bg="light"
                      text="dark"
                      className="border flex-shrink-0"
                    >
                      {entry.tag}
                    </Badge>
                  )}
                  <span
                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    <LogLineText text={entry.text} />
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Evaluation tab */}
        {tab === "evaluation" && (
          <>
            {evalLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            )}
            {!evalLoading && !evaluation && (
              <p className="text-muted small">
                No evaluation linked to this run.
              </p>
            )}
            {!evalLoading && evaluation && (
              <>
                {/* Summary row */}
                <Row className="mb-3 g-2">
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">Original total</small>
                    <strong>
                      {evaluation.originalTotal != null
                        ? `${evaluation.currency ?? ""}${evaluation.originalTotal.toFixed(2)}`
                        : "—"}
                    </strong>
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">Best saving</small>
                    <strong className={bestSaving > 0 ? "text-success" : ""}>
                      {bestSaving > 0
                        ? `${evaluation.currency ?? ""}${bestSaving.toFixed(2)}`
                        : "—"}
                    </strong>
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">Mode</small>
                    <Badge
                      bg={
                        evaluation.templateMode === "new" ? "warning" : "info"
                      }
                      text="dark"
                    >
                      {evaluation.templateMode}
                    </Badge>
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">AI called</small>
                    {evaluation.aiCalled ? (
                      <Badge bg="primary">Yes</Badge>
                    ) : (
                      <span className="text-muted small">No</span>
                    )}
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">View</small>
                    <ViewBadge view={evaluation.templateView} />
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">Auto version</small>
                    <strong className="small">
                      {evaluation.version != null
                        ? `v${evaluation.version}`
                        : "—"}
                    </strong>
                  </Col>
                </Row>

                {/* Coupon codes */}
                <h6 className="mb-2">
                  Coupon codes ({evaluation.codes.length})
                </h6>
                {evaluation.codes.length === 0 ? (
                  <p className="text-muted small">No codes evaluated.</p>
                ) : (
                  <Table size="sm" bordered responsive className="mb-3">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Result</th>
                        <th>Savings</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evaluation.codes.map((code) => (
                        <tr key={code.id}>
                          <td className="font-monospace small">{code.code}</td>
                          <td>
                            {code.notApplicableToBasket ? (
                              <Badge bg="info">Not Applicable</Badge>
                            ) : code.success ? (
                              <Badge bg="success">Applied</Badge>
                            ) : (
                              <Badge bg="danger">Failed</Badge>
                            )}
                          </td>
                          <td>
                            {code.success && code.savings > 0
                              ? `${evaluation.currency ?? ""}${code.savings.toFixed(2)}`
                              : "—"}
                          </td>
                          <td className="small text-muted">
                            {code.errorMessage ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}

                {/* Checkout-page URL regexes matched by this run */}
                {(evaluation.checkoutPageUrlRegex ||
                  evaluation.skuCheckoutPageUrlRegex) && (
                  <Row className="mb-3 g-2">
                    <Col xs={12} md={6}>
                      <small className="text-muted d-block">
                        Auto checkout URL regex
                      </small>
                      <code className="small text-break">
                        {evaluation.checkoutPageUrlRegex ?? "—"}
                      </code>
                    </Col>
                    <Col xs={12} md={6}>
                      <small className="text-muted d-block">
                        SKU cart URL regex
                      </small>
                      <code className="small text-break">
                        {evaluation.skuCheckoutPageUrlRegex ?? "—"}
                      </code>
                    </Col>
                  </Row>
                )}

                {/* Coupon selectors used */}
                {evaluation.template && (
                  <>
                    <h6 className="mb-2">Coupon selectors used</h6>
                    <pre
                      className="bg-light p-2 rounded small"
                      style={{
                        maxHeight: 200,
                        overflowY: "auto",
                        fontSize: 11,
                      }}
                    >
                      {JSON.stringify(evaluation.template, null, 2)}
                    </pre>
                  </>
                )}

                {/* SKU selectors — prefer the ones this run actually captured
                    (evaluation.skuTemplate); fall back to the domain's current
                    live/staging SKU selectors when the run stored none. */}
                {(evaluation.skuTemplate ?? skuSelectors) && (
                  <>
                    <h6 className="mb-2 mt-3">
                      SKU selectors{" "}
                      {!evaluation.skuTemplate && skuSelectors && (
                        <span className="text-muted small fw-normal">
                          (current template — run stored none)
                        </span>
                      )}
                    </h6>
                    <pre
                      className="bg-light p-2 rounded small"
                      style={{
                        maxHeight: 200,
                        overflowY: "auto",
                        fontSize: 11,
                      }}
                    >
                      {JSON.stringify(
                        evaluation.skuTemplate ?? skuSelectors,
                        null,
                        2
                      )}
                    </pre>
                  </>
                )}

                {/* Captured cart items */}
                {evaluation.capturedItems.length > 0 && (
                  <>
                    <h6 className="mb-2 mt-3">
                      Captured cart items ({evaluation.capturedItems.length})
                    </h6>
                    <Table size="sm" bordered responsive className="mb-3">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Qty</th>
                          <th>Price</th>
                          <th>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evaluation.capturedItems.map((item, i) => (
                          <tr key={i}>
                            <td className="small">
                              {item.productUrl ? (
                                <a
                                  href={item.productUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {item.name}
                                </a>
                              ) : (
                                item.name
                              )}
                            </td>
                            <td>{item.quantity}</td>
                            <td>
                              {item.price != null
                                ? `${evaluation.currency ?? ""}${item.price.toFixed(2)}`
                                : "—"}
                            </td>
                            <td className="small text-muted">
                              {item.category ?? "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

// ─── Reporting tab (AI cost analytics) ─────────────────────────────────────

const COST_DAY_RANGE_OPTIONS = [1, 7, 14, AUTO_COUPON_COST_REPORT_MAX_DAYS];

/** "24h" reads clearer than "1d" for the shortest window — everything else
 *  is just "{n}d". Note this is the UTC calendar day so far (same bucketing
 *  as every other window), not a true rolling 24-hour window — so right
 *  after UTC midnight it can look sparse. */
const dayRangeLabel = (d: number): string => (d === 1 ? "24h" : `${d}d`);

/** Human label for the currently-selected window, used to make the KPI
 *  section's time scope unambiguous (avoids "is this all-time or today?"). */
const windowLabel = (d: number): string =>
  d === 1 ? "last 24 hours (UTC calendar day)" : `last ${d} days`;

/** `badgeBg`/`badgeText` distinguish *why* a domain was flagged at a glance —
 *  "danger" (red) for the two dollar-driven flags, "warning" (amber) for the
 *  one behavior-driven flag (run count, not a spend threshold). Thresholds
 *  are spelled out in each hint so "why was this flagged" never requires
 *  reading source code — keep these in sync with
 *  packages/backend/src/constants/ai-cost-flags.ts if they change. */
const FLAG_META: Record<
  Exclude<TAutoCouponCostFlag, null>,
  { label: string; hint: string; badgeBg: string; badgeText?: "dark" }
> = {
  repeated_no_success: {
    label: "Repeated, no success",
    hint: "3+ AI-costing runs in this window, 0 successful coupons",
    badgeBg: "warning",
    badgeText: "dark",
  },
  high_unevaluated_spend: {
    label: "High unevaluated spend",
    hint: "$3+ spent in this window, and at least half of it went to runs that never completed an evaluation",
    badgeBg: "danger",
  },
};

/** Reuses the exact labels already shown in the Live tab's "Run by" filter
 *  (above), so the breakdown table doesn't invent new copy for the same
 *  concept. */
const ORIGIN_LABELS: Record<string, string> = {
  extension: "User (extension)",
  session: "Agentic (session)",
  capture: "Agentic (capture)",
};

/** $0.0031-style precision for tiny per-call averages, $142.50-style
 *  otherwise — plain .toFixed(2) alone would render genuinely-nonzero
 *  sub-cent figures as a misleading "$0.00". */
const formatUsd = (value: number): string =>
  value > 0 && value < 0.01 ? `$${value.toFixed(4)}` : `$${value.toFixed(2)}`;

const RunsReportingTab: React.FC = () => {
  const [days, setDays] = useState(1);
  const [metric, setMetric] = useState<"cost" | "calls">("cost");
  const [breakdownBy, setBreakdownBy] = useState<"domain" | "origin">("domain");
  const [downloading, setDownloading] = useState<"daily" | "domain" | null>(
    null
  );
  const [refreshing, setRefreshing] = useState(false);

  // useGetLazy (manual execute) rather than useGet's auto-refetch, so a
  // "force refresh" can hit `&refresh=true` for one call without that flag
  // getting stuck on the URL for every later day-range switch — mirrors the
  // pattern already used by SessionStatsCards on the Session Merchants page.
  const { data, loading, error, execute } =
    useGetLazy<IAutoCouponRunCostReportResponse>();
  const { execute: fetchCsv } = useGetLazy<string>();

  useEffect(() => {
    execute(`/auto-coupons/runs/cost-report?days=${days}`).catch(() => {
      // Surfaced via the `error` state below.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const entries = data?.daily ?? [];
  const totals = data?.totals;
  const flaggedDomains = data?.flaggedDomains ?? [];

  const aiRunPct =
    totals && totals.totalRuns > 0
      ? Math.round((totals.runsWithCost / totals.totalRuns) * 100)
      : 0;

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await execute(`/auto-coupons/runs/cost-report?days=${days}&refresh=true`);
    } catch {
      // Surfaced via the `error` state below.
    } finally {
      setRefreshing(false);
    }
  };

  const download = async (mode: "daily" | "domain") => {
    setDownloading(mode);
    try {
      const csv = await fetchCsv(
        `/auto-coupons/runs/cost-report/export?days=${days}&mode=${mode}`,
        { responseType: "text" }
      );
      if (!csv) return;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = data?.latestDate ?? new Date().toISOString().slice(0, 10);
      a.download = `runs-cost-report-${
        mode === "domain" ? "by-domain" : "daily"
      }-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div>
      {/* Controls */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <span className="small text-muted">Window:</span>
          <ButtonGroup size="sm">
            {COST_DAY_RANGE_OPTIONS.map((d) => (
              <Button
                key={d}
                variant={d === days ? "primary" : "outline-secondary"}
                onClick={() => setDays(d)}
              >
                {dayRangeLabel(d)}
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <RefreshCw size={14} className="me-1" />
            )}
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Dropdown as={ButtonGroup} size="sm">
            <Button
              variant="outline-success"
              onClick={() => download("daily")}
              disabled={!!downloading || entries.length === 0}
              title="Export one row per day (summary)"
            >
              {downloading === "daily" ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Download size={14} className="me-1" />
              )}
              CSV: summary by day
            </Button>
            <Dropdown.Toggle
              split
              variant="outline-success"
              disabled={!!downloading || entries.length === 0}
            />
            <Dropdown.Menu align="end">
              <Dropdown.Item
                onClick={() => download("domain")}
                disabled={!!downloading}
                title="Export one row per domain (whole window)"
              >
                {downloading === "domain" ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <Download size={13} className="me-2" />
                )}
                CSV: by domain
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}
      {error && <Alert variant="danger">Failed to load report.</Alert>}

      {!error && data && totals && (
        <>
          {/* ── Stat cards — every figure below is scoped to this window,
                nothing here is lifetime/all-time ── */}
          <div className="small text-muted mb-1 text-uppercase">
            All figures below: {windowLabel(days)}
          </div>
          <Row className="g-2 mb-3">
            <ReportStatCard
              label="Total AI cost"
              value={formatUsd(totals.totalCost)}
              color="primary"
              sub={dayRangeLabel(days)}
            />
            <ReportStatCard
              label="Avg cost / AI run"
              value={formatUsd(totals.avgCostPerRun)}
              color="dark"
            />
            <ReportStatCard
              label="Total LLM calls"
              value={totals.totalCalls}
              color="secondary"
            />
            <ReportStatCard
              label="Runs with AI cost"
              value={totals.runsWithCost}
              color="dark"
              sub={`${aiRunPct}% of ${totals.totalRuns} runs`}
            />
            <ReportStatCard
              label="Domains flagged"
              value={flaggedDomains.length}
              color={flaggedDomains.length > 0 ? "warning" : "success"}
            />
          </Row>

          {/* ── Needs attention ── */}
          {flaggedDomains.length > 0 && (
            <Card className="mb-3 border-warning">
              <Card.Header className="py-2 bg-warning bg-opacity-10">
                <span className="fw-semibold small">
                  Needs attention ({flaggedDomains.length} domain
                  {flaggedDomains.length === 1 ? "" : "s"})
                </span>
              </Card.Header>
              <Card.Body className="p-0">
                {/* Capped to ~5 visible rows, scrollable beyond that — this
                    list has no server-side limit (grows with however many
                    domains actually match a flag), so it needs to stay
                    usable as it grows rather than pushing the rest of the
                    page down indefinitely. */}
                <div style={{ maxHeight: 195, overflowY: "auto" }}>
                  <Table size="sm" hover className="mb-0 small">
                    <tbody>
                      {flaggedDomains.map((d) => (
                        <tr key={d.domain}>
                          <td className="fw-semibold">{d.domain}</td>
                          <td>
                            {d.flag && (
                              <Badge
                                bg={FLAG_META[d.flag].badgeBg}
                                text={FLAG_META[d.flag].badgeText}
                                title={FLAG_META[d.flag].hint}
                              >
                                {FLAG_META[d.flag].label}
                              </Badge>
                            )}
                          </td>
                          <td className="text-muted">
                            {d.flag === "high_unevaluated_spend" ? (
                              <>
                                {formatUsd(d.totalCost)} spent ·{" "}
                                {Math.round(d.unevaluatedCostShare * 100)}%
                                unevaluated
                              </>
                            ) : (
                              <>
                                {d.runsWithCost} run
                                {d.runsWithCost === 1 ? "" : "s"} ·{" "}
                                {formatUsd(d.totalCost)} ·{" "}
                                {d.successfulCoupons} successful coupon
                                {d.successfulCoupons === 1 ? "" : "s"}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* ── Trend chart: metric switcher instead of dual-axis, since cost
                and call-count are different units ── */}
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center py-2 flex-wrap gap-2">
              <span className="fw-semibold small">
                Daily trend ({dayRangeLabel(days)})
              </span>
              <ButtonGroup size="sm">
                <Button
                  variant={metric === "cost" ? "primary" : "outline-secondary"}
                  onClick={() => setMetric("cost")}
                >
                  Cost ($)
                </Button>
                <Button
                  variant={metric === "calls" ? "primary" : "outline-secondary"}
                  onClick={() => setMetric("calls")}
                >
                  LLM calls (#)
                </Button>
              </ButtonGroup>
            </Card.Header>
            <Card.Body className="p-2">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart
                  data={entries}
                  margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => String(v).slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    allowDecimals={metric === "cost"}
                    tickFormatter={(v) =>
                      metric === "cost" ? `$${v}` : String(v)
                    }
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    labelFormatter={(l) => `Date: ${l}`}
                    formatter={(v: number | undefined) =>
                      metric === "cost"
                        ? [v != null ? formatUsd(v) : "—", "Cost"]
                        : [v ?? 0, "LLM calls"]
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey={metric === "cost" ? "totalCost" : "totalCalls"}
                    name={metric === "cost" ? "Cost" : "LLM calls"}
                    stroke={metric === "cost" ? "#0d6efd" : "#20c997"}
                    strokeWidth={2}
                    dot={entries.length <= 31}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>

          {/* ── Breakdown: domain vs origin toggle ── */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center py-2 flex-wrap gap-2">
              <span className="fw-semibold small">
                Breakdown (
                {breakdownBy === "domain" ? "top domains" : "by origin"})
              </span>
              <ButtonGroup size="sm">
                <Button
                  variant={
                    breakdownBy === "domain" ? "primary" : "outline-secondary"
                  }
                  onClick={() => setBreakdownBy("domain")}
                >
                  By domain
                </Button>
                <Button
                  variant={
                    breakdownBy === "origin" ? "primary" : "outline-secondary"
                  }
                  onClick={() => setBreakdownBy("origin")}
                >
                  By origin
                </Button>
              </ButtonGroup>
            </Card.Header>
            <Card.Body className="p-0">
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                <Table size="sm" hover className="mb-0 small">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>{breakdownBy === "domain" ? "Domain" : "Origin"}</th>
                      <th>Total cost</th>
                      <th>LLM calls</th>
                      <th>Runs w/ AI cost</th>
                      <th>Total runs</th>
                      <th>Avg cost/run</th>
                      {breakdownBy === "domain" && (
                        <>
                          <th>Cost/success</th>
                          <th>Flag</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {breakdownBy === "domain"
                      ? (data.byDomain ?? []).map(
                          (d: IAutoCouponCostByDomainEntry) => (
                            <tr
                              key={d.domain}
                              className={
                                d.flag
                                  ? `bg-${FLAG_META[d.flag].badgeBg} bg-opacity-10`
                                  : undefined
                              }
                            >
                              <td className="fw-semibold">{d.domain}</td>
                              <td>{formatUsd(d.totalCost)}</td>
                              <td>{d.totalCalls}</td>
                              <td>{d.runsWithCost}</td>
                              <td>{d.totalRuns}</td>
                              <td>{formatUsd(d.avgCostPerRun)}</td>
                              <td>
                                {d.costPerSuccess != null
                                  ? formatUsd(d.costPerSuccess)
                                  : "—"}
                              </td>
                              <td>
                                {d.flag && (
                                  <Badge
                                    bg={FLAG_META[d.flag].badgeBg}
                                    text={FLAG_META[d.flag].badgeText}
                                    title={FLAG_META[d.flag].hint}
                                  >
                                    {FLAG_META[d.flag].label}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          )
                        )
                      : (data.byOrigin ?? []).map((o) => (
                          <tr key={o.origin}>
                            <td>
                              <Badge bg="secondary">
                                {ORIGIN_LABELS[o.origin] ?? o.origin}
                              </Badge>
                            </td>
                            <td>{formatUsd(o.totalCost)}</td>
                            <td>{o.totalCalls}</td>
                            <td>{o.runsWithCost}</td>
                            <td>{o.totalRuns}</td>
                            <td>{formatUsd(o.avgCostPerRun)}</td>
                          </tr>
                        ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────

export const AutoCouponRuns: React.FC = () => {
  const [view, setView] = useState<"live" | "reporting">("live");
  const [page, setPage] = useState(1);
  const [domainFilter, setDomainFilter] = useState("");
  const [originFilter, setOriginFilter] = useState<
    "" | "extension" | "session" | "capture"
  >("");
  const [selectedRun, setSelectedRun] = useState<IAutoCouponRun | null>(null);

  // Deep links (e.g. "Run logs →" from the Session Runs page) pin a single run
  // by correlationId so a failed run jumps straight to its logs.
  const [searchParams] = useSearchParams();
  const correlationId = searchParams.get("correlationId") ?? "";

  const queryParams = new URLSearchParams({ page: String(page), limit: "25" });
  if (domainFilter) queryParams.set("domain", domainFilter);
  if (originFilter) queryParams.set("origin", originFilter);
  if (correlationId) queryParams.set("correlationId", correlationId);

  const { data, loading, error, refetch } = useGet<IAutoCouponRunListResponse>(
    `/auto-coupons/runs?${queryParams}`
  );

  const runs = data?.runs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 25);

  // Open the log modal automatically the first time a deep-linked run loads.
  // Guarded so the user can close it (and re-open manually) without it popping
  // back up on the next poll/refetch.
  const autoOpenedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!correlationId || autoOpenedRef.current === correlationId) return;
    const match = runs.find((r) => r.correlationId === correlationId);
    if (match) {
      setSelectedRun(match);
      autoOpenedRef.current = correlationId;
    }
  }, [correlationId, runs]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Auto Coupon Runs</h4>
        {view === "live" && (
          <Button variant="outline-primary" size="sm" onClick={() => refetch()}>
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
        )}
      </div>

      <Nav
        variant="tabs"
        activeKey={view}
        onSelect={(k) => setView((k ?? "live") as "live" | "reporting")}
        className="mb-3"
      >
        <Nav.Item>
          <Nav.Link eventKey="live">Live</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="reporting">Reporting</Nav.Link>
        </Nav.Item>
      </Nav>

      {view === "reporting" && <RunsReportingTab />}

      {view === "live" && (
        <>
          {correlationId && (
            <Alert
              variant="info"
              className="py-2 small d-flex align-items-center justify-content-between"
            >
              <span>
                Showing a single run —{" "}
                <span className="font-monospace">{correlationId}</span>
              </span>
              <Link to="/auto-coupons/runs" className="text-decoration-none">
                Clear filter
              </Link>
            </Alert>
          )}

          <Card className="mb-3">
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Label className="small">Domain</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Filter by domain…"
                    value={domainFilter}
                    onChange={(e) => {
                      setDomainFilter(e.target.value);
                      setPage(1);
                    }}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Run by</Form.Label>
                  <Form.Select
                    size="sm"
                    value={originFilter}
                    onChange={(e) => {
                      setOriginFilter(e.target.value as typeof originFilter);
                      setPage(1);
                    }}
                  >
                    <option value="">All</option>
                    <option value="extension">User (extension)</option>
                    <option value="session">Agentic (session)</option>
                    <option value="capture">Agentic (capture)</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          )}
          {error && <Alert variant="danger">Failed to load runs.</Alert>}

          {!loading && !error && (
            <>
              <Table hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Correlation ID</th>
                    <th>Domain</th>
                    <th>Run by</th>
                    <th>User</th>
                    <th title="Template mode (new/template), and which half(s) of the checkout flow the run exercised (both/auto-only/sku-only)">
                      Mode
                    </th>
                    <th>Logs</th>
                    <th title="Manual QA verdict from the linked evaluation — independent of whether the automated coupon-testing loop finished">
                      QA (reviewer)
                    </th>
                    <th>Coupons</th>
                    <th>Best saving</th>
                    <th>AI cost</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center text-muted py-4">
                        No runs found.
                      </td>
                    </tr>
                  )}
                  {runs.map((run) => (
                    <tr
                      key={run._id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedRun(run)}
                    >
                      <td className="font-monospace small text-muted">
                        {run.correlationId.slice(0, 8)}…
                      </td>
                      <td>
                        {run.domain}
                        {run.abandoned && (
                          <span
                            className="ms-1"
                            title={
                              run.abandonedReason
                                ? `Run abandoned: ${run.abandonedReason}`
                                : "Run abandoned — the tab went away mid-run, or a verdict was never submitted"
                            }
                          >
                            <AlertTriangle
                              size={13}
                              color="#dc3545"
                              style={{ verticalAlign: "text-top" }}
                            />
                          </span>
                        )}
                      </td>
                      <td>
                        {run.origin === "extension" ? (
                          <Badge bg="light" text="dark">
                            User
                          </Badge>
                        ) : (
                          <Badge bg="dark">Agentic</Badge>
                        )}
                        {run.sessionCaptureOnly && (
                          <Badge
                            bg="secondary"
                            className="ms-1"
                            title="No AI stages ran and no coupon codes were attempted — this run only captured the browser session"
                          >
                            Session only
                          </Badge>
                        )}
                      </td>
                      <td className="small text-muted">
                        {run.userEmail ?? "—"}
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1 align-items-start">
                          {run.templateMode ? (
                            <Badge
                              bg={
                                run.templateMode === "new" ? "warning" : "info"
                              }
                              text="dark"
                            >
                              {run.templateMode}
                            </Badge>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                          {run.runMode && (
                            <Badge
                              bg="light"
                              text="dark"
                              className="border"
                              title="Which half(s) of the checkout flow this run exercised"
                            >
                              {run.runMode}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td>{run.logs?.length ?? 0}</td>
                      <td>
                        {run.reviewerVerdict ? (
                          <div className="d-flex gap-1 align-items-center small">
                            <span className="text-muted">A</span>
                            <PartBadge
                              status={run.reviewerVerdict.autoTemplateStatus}
                              applicable={run.runMode !== "sku-only"}
                              notTestedHint="Reviewer did not rate the auto template"
                            />
                            <span className="text-muted ms-1">S</span>
                            <PartBadge
                              status={run.reviewerVerdict.skuTemplateStatus}
                              applicable={run.runMode !== "auto-only"}
                              notTestedHint="Reviewer did not rate the SKU template"
                            />
                          </div>
                        ) : (
                          <span
                            className="text-muted"
                            title="No manual QA verdict recorded for this run"
                          >
                            Not reviewed
                          </span>
                        )}
                      </td>
                      <td>
                        {run.couponsEvaluated > 0 ? (
                          <span>
                            <span className="text-success">
                              {run.successfulCoupons}
                            </span>
                            {" / "}
                            {run.couponsEvaluated}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {run.bestSavings > 0
                          ? `${run.currency ?? ""}${run.bestSavings.toFixed(2)}`
                          : "—"}
                      </td>
                      <td className="small text-muted">
                        {run.aiCost != null ? `$${run.aiCost.toFixed(2)}` : "—"}
                      </td>
                      <td className="small text-muted">
                        {new Date(run.createdAt).toLocaleString()}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          title="View details"
                          onClick={() => setSelectedRun(run)}
                        >
                          <List size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">{total} total</small>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Prev
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      <RunDetailModal run={selectedRun} onClose={() => setSelectedRun(null)} />
    </div>
  );
};

// ─── Log viewer modal ──────────────────────────────────────────────────────

const LogsModal: React.FC<{
  run: IAutoCouponRun | null;
  onClose: () => void;
}> = ({ run, onClose }) => {
  const levelColor: Record<string, string> = {
    error: "text-danger",
    warn: "text-warning",
    info: "text-primary",
    debug: "text-muted",
    verbose: "text-muted",
  };

  return (
    <Modal show={!!run} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Run logs — {run?.domain} ({run?.correlationId?.slice(0, 8)}…)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: 600, overflowY: "auto" }}>
        {run?.logs?.length === 0 && (
          <p className="text-muted">No log entries.</p>
        )}
        {run?.logs?.map((entry, i) => (
          <div
            key={i}
            className={`d-flex gap-2 small font-monospace mb-1 ${levelColor[entry.level] ?? ""}`}
          >
            <span className="text-muted flex-shrink-0">
              {new Date(entry.timestamp).toLocaleTimeString()}
            </span>
            <span
              className="text-secondary flex-shrink-0 text-uppercase"
              style={{ width: 50 }}
            >
              [{entry.level}]
            </span>
            <span>{entry.text}</span>
          </div>
        ))}
      </Modal.Body>
    </Modal>
  );
};
